import { useCallback, useEffect, useMemo, useState } from 'react';
import { ensureAsciiDateString } from '../../../shared/utils/apiParams';
import { useGuestMenuSize } from '../config/featureFlags';
import {
  buildGuestDisplayMenuDates,
  GUEST_MENU_INFINITE_INITIAL_FUTURE,
  resolveGuestInfiniteFutureExtent,
  resolveGuestMenuSize,
  type ResolvedGuestMenuSize,
} from './guestMenuSize';

export function useGuestDisplayMenuDates(): {
  dates: string[];
  is_infinite: boolean;
  resolved: ResolvedGuestMenuSize;
  ensureGuestDateInRange: (date_raw: string) => void;
  resolveGuestDatesIncluding: (date_raw?: string) => string[];
} {
  const guest_menu_size = useGuestMenuSize();
  const resolved = useMemo(() => resolveGuestMenuSize(guest_menu_size), [guest_menu_size]);
  const [infinite_future_days, set_infinite_future_days] = useState(GUEST_MENU_INFINITE_INITIAL_FUTURE);

  useEffect(() => {
    set_infinite_future_days(resolved.future_days);
  }, [resolved.mode, resolved.future_days]);

  const dates = useMemo(
    () =>
      buildGuestDisplayMenuDates(
        resolved,
        undefined,
        resolved.mode === 'infinite' ? infinite_future_days : undefined,
      ),
    [resolved, infinite_future_days],
  );

  const ensureGuestDateInRange = useCallback(
    (date_raw: string) => {
      if (resolved.mode !== 'infinite') {
        return;
      }
      const date_ascii = ensureAsciiDateString(date_raw) || date_raw;
      if (date_ascii.length < 10) {
        return;
      }
      set_infinite_future_days(prev => resolveGuestInfiniteFutureExtent(date_ascii, prev));
    },
    [resolved.mode],
  );

  const resolveGuestDatesIncluding = useCallback(
    (date_raw?: string) => {
      if (resolved.mode !== 'infinite' || !date_raw) {
        return dates;
      }
      const date_ascii = ensureAsciiDateString(date_raw) || date_raw;
      if (date_ascii.length < 10) {
        return dates;
      }
      const needed_future = resolveGuestInfiniteFutureExtent(date_ascii, infinite_future_days);
      return buildGuestDisplayMenuDates(resolved, undefined, needed_future);
    },
    [resolved, infinite_future_days, dates],
  );

  return {
    dates,
    is_infinite: resolved.mode === 'infinite',
    resolved,
    ensureGuestDateInRange,
    resolveGuestDatesIncluding,
  };
}
